import './Banner.css';
import Form, { FormProps } from '..';
import { DropdownItem } from '../../Input/InputDropdown';
import useStore from '../../Hooks/useStore';
import React, { useState } from 'react';
import Element from '../../Element';
import { getFilterColumn, setFilterColumn } from '../../../store/utils';
import { useSnackbar } from 'notistack';

interface BannerDescriptionProps extends FormProps {}

export default function BannerDescription({
  children,
  className = '',
  ...rest
}: BannerDescriptionProps) {
  const [getters, setters] = useStore();

  const {
    form: { getShowSingleForm, isBatchMode, getElements },
    banner: { getBanner, getUnits },
  } = getters;

  const {
    form: { setBatchForm },
    product: {
      findProduct,
      findFilteredProducts,
      findFilteredPromotionalProducts,
      autocompletePromotionalProducts,
      updateSingleFormForProduct,
    },
    banner: { setBanner },
  } = setters;

  const elements = getElements('product');

  const onSubmit = (e: any) => {
    e.preventDefault();
  };

  const [productLoading, setProductLoading] = useState(false);
  const [autocompletePromotionLoading, setAutocompletePromotionLoading] =
    useState(false);
  const [autocompleteTypeLoading, setAutocompleteTypeLoading] = useState(false);

  const descriptionTypeItems = [
    {
      id: 'single',
      name: 'Única',
    },
    {
      id: 'double',
      name: 'Dupla',
    },
  ];

  const duplicity = getShowSingleForm('product.duplicity');

  const productType = getShowSingleForm('product.type');
  const { enqueueSnackbar } = useSnackbar();

  const findNormalFilteredProducts = (
    searchFilter = getShowSingleForm('config.filter'),
  ) => {
    setProductLoading(true);
    findFilteredProducts(searchFilter)
      .then((response) => {
        setBanner({
          field: 'items.products',
          value: response.data.map((data) => ({
            id: data.id,
            name: `${data.code} - ${data.resume?.trim()} - ${data.ean.padStart(
              13,
              '0',
            )}`,
            data,
          })),
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar os produtos', {
          variant: 'error',
        });
      })
      .finally(() => {
        setProductLoading(false);
      });
  };

  const findPromotionFilteredProducts = (
    searchFilter = getShowSingleForm('config.filter'),
  ) => {
    setProductLoading(true);
    findFilteredPromotionalProducts(searchFilter)
      .then((response) => {
        setBanner({
          field: 'items.products',
          value: response.data.map((data) => ({
            id: data.id,
            name: `${data.code} - ${data.resume.trim()} - ${data.ean.padStart(
              13,
              '0',
            )}${
              data.promotion?.type ? ` - ${data.promotion.type.trim()}` : ''
            } - ${data.promotion?.description?.trim() ?? 'GERAL'}`,
            data,
          })),
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar os produtos', {
          variant: 'error',
        });
      })
      .finally(() => {
        setProductLoading(false);
      });
  };

  const [productListFocused, setProductListFocused] = React.useState(false);
  const [promotionListFocused, setPromotionListFocused] = React.useState(false);
  const [typeListFocused, setTypeListFocused] = React.useState(false);

  const onProductListHandler = () => {
    switch (productType) {
      case 'normal':
        findNormalFilteredProducts();
        break;
      case 'promotion':
        findPromotionFilteredProducts();
        break;
      default:
        break;
    }
  };

  const searchFilter = getShowSingleForm('config.filter');

  const setSearchFilter = (filter: SearchFilter) =>
    setBatchForm('config.filter', filter);

  const promotionAutocompleteFilter = getShowSingleForm(
    'config.autocompletes.promotion.filter',
  );

  const setPromotionAutocompleteFilter = (filter?: Filter[]) =>
    setBatchForm('config.autocompletes.promotion.filter', filter);

  const promotionFilter = getShowSingleForm(
    'config.autocompletes.promotion.value',
  );

  const typeFilter = getShowSingleForm('config.autocompletes.type.value');

  const setPromotionFilter = (e) => {
    const value = e.target.value;
    setBatchForm('config.autocompletes.promotion.value', value);
    setSearchFilter(
      setFilterColumn('promotion', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const setTypeFilter = (e) => {
    const value = e.target.value;
    setBatchForm('config.autocompletes.type.value', value);
    setSearchFilter(
      setFilterColumn('type', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const productFilter = getFilterColumn('resume', searchFilter);

  const setProductFilter = (value: string) => {
    let filter = { ...searchFilter };
    filter = setFilterColumn('ean', value, filter, 'LIKE', 'OR');
    filter = setFilterColumn('code', value, filter, 'LIKE', 'OR');
    filter = setFilterColumn('resume', value, filter, 'LIKE', 'OR');
    setSearchFilter(filter);
  };

  const onSelectHandler = ({
    data,
  }: DropdownItem<FilteredPromotionalProduct>) => {
    const { id: productFilialId, promotionalProductId } = data!;
    const findProductInput = promotionalProductId
      ? { promotionalProductId }
      : { productFilialId };

    findProduct(findProductInput).then((product) => {
      const price = product.productfilials[0].prices[0];
      const promotionalProduct =
        product.productfilials[0]?.promotionalProducts?.[0];
      updateSingleFormForProduct({
        id: product.id,
        type: productType,
        ean: product.ean,
        incomingPackaging: product.productfilials[0].incomingPackaging,
        exitPackaging: product.productfilials[0].exitPackaging,
        code: String(product.productfilials[0].code),
        description: product.productfilials[0].description,
        resume: product.productfilials[0].resume,
        filial: '',
        price: {
          id: price.id,
          regularPrice: price.regularPrice,
          promotionalPrice: price.promotionalPrice ?? 0,
          promotionalStartDate: price.promotionalStartDate,
          promotionalEndDate: price.promotionalEndDate,
        },
        promotion: promotionalProduct
          ? {
              id: promotionalProduct.id,
              type: promotionalProduct.promotion.type,
              description: promotionalProduct.promotion.description,
              promotionalPrice: promotionalProduct.promotionalPrice,
              promotionalStartDate: promotionalProduct.promotion.startDate,
              promotionalEndDate: promotionalProduct.promotion.endDate,
            }
          : undefined,
        identifier: 0,
      });
    });
  };

  let promotionTimerId: NodeJS.Timeout;

  const handleAutocompletePromotionFilter = () => {
    setAutocompletePromotionLoading(true);
    autocompletePromotionalProducts(
      getShowSingleForm('config.autocompletes.promotion'),
    )
      .then((promotions) => {
        setBanner({
          field: 'items.promotion',
          value: promotions.map((name, id) => ({ id, name })),
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar as promoções', {
          variant: 'error',
        });
      })
      .finally(() => {
        setAutocompletePromotionLoading(false);
      });
  };

  React.useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(promotionTimerId);
      promotionTimerId = setTimeout(() => {
        if (promotionListFocused) handleAutocompletePromotionFilter();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(promotionTimerId);
    };
  }, [promotionFilter, promotionListFocused]);

  let typeTimerId: NodeJS.Timeout;

  const handleAutocompletePromotionTypeFilter = () => {
    setAutocompleteTypeLoading(true);
    autocompletePromotionalProducts(
      getShowSingleForm('config.autocompletes.type'),
    )
      .then((types) => {
        setBanner({
          field: 'items.type',
          value: types.map((name, id) => ({ id, name })),
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao buscar as promoções', {
          variant: 'error',
        });
      })
      .finally(() => {
        setAutocompleteTypeLoading(false);
      });
  };

  React.useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(typeTimerId);
      typeTimerId = setTimeout(() => {
        if (typeListFocused) handleAutocompletePromotionTypeFilter();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(typeTimerId);
    };
  }, [typeFilter, typeListFocused]);

  let productTimerId: NodeJS.Timeout;

  React.useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(productTimerId);
      productTimerId = setTimeout(() => {
        if (productListFocused) onProductListHandler();
      }, 300);
    };
    handleInputChange();
    return () => {
      clearTimeout(productTimerId);
    };
  }, [searchFilter, productListFocused]);

  const setPromotion = (value: string) => {
    setBatchForm('config.autocompletes.promotion.value', value);
    setSearchFilter(
      setFilterColumn('promotion', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const setType = (value: string) => {
    setBatchForm('config.autocompletes.type.value', value);
    setSearchFilter(
      setFilterColumn('type', value, searchFilter, 'LIKE', 'AND'),
    );
  };

  const isBatch = isBatchMode();

  return (
    <Form.Banner
      className={`form-banner ${className}`}
      onSubmit={onSubmit}
      {...rest}
    >
      <Form.Input.Checkbox
        className="form-banner-field"
        errorMessage={false}
        checked={getShowSingleForm('product.type') === 'promotion'}
        disabled={isBatch}
        onChange={(e) => {
          const productType = e.target.checked ? 'promotion' : 'normal';
          setBatchForm('product.type', productType);
          if (productType === 'normal') {
            const filter = setFilterColumn(
              'promotion',
              '',
              searchFilter,
              'LIKE',
              'AND',
            );
            setSearchFilter(setFilterColumn('type', '', filter, 'LIKE', 'AND'));
          }
          if (productType === 'promotion') {
            setSearchFilter(
              setFilterColumn(
                'promotion',
                promotionFilter,
                searchFilter,
                'LIKE',
                'AND',
              ),
            );
          }
        }}
        label="Preços Promocionais"
      />

      <Form.Input.Dropdown
        className="form-banner-field"
        errorMessage={false}
        items={getBanner('items.products')}
        disabled={isBatch}
        label="Busque o produto"
        loading={productLoading}
        value={productFilter as string}
        onChange={(e) => {
          setProductFilter(e.target.value);
        }}
        onFocus={() => {
          setProductListFocused(true);
          setProductFilter('');
        }}
        onBlur={() => {
          setProductListFocused(false);
        }}
        onSelected={(item) => {
          setProductListFocused(false);
          onSelectHandler(item);
          setProductFilter(`${item.name}`);
        }}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
        }}
      >
        {getShowSingleForm('product.type') === 'promotion' ? (
          <>
            <Form.Input.Dropdown
              className="form-banner-field"
              label="Tipo da promoção"
              errorMessage={false}
              items={getBanner('items.type')}
              value={typeFilter}
              loading={autocompleteTypeLoading}
              disabled={isBatch}
              hasClearItem
              onChange={(e) => {
                setTypeFilter(e);
                setPromotionAutocompleteFilter(
                  setFilterColumn(
                    'type',
                    e.target.value,
                    { filter: promotionAutocompleteFilter },
                    'LIKE',
                    'AND',
                  ).filter,
                );
                setSearchFilter(
                  setFilterColumn(
                    'type',
                    e.target.value,
                    searchFilter,
                    'LIKE',
                    'AND',
                  ),
                );
              }}
              onFocus={() => {
                setTypeListFocused(true);
                setType('');
                setPromotionAutocompleteFilter(
                  setFilterColumn(
                    'type',
                    '',
                    { filter: promotionAutocompleteFilter },
                    'LIKE',
                    'AND',
                  ).filter,
                );
                setSearchFilter(
                  setFilterColumn('type', '', searchFilter, 'LIKE', 'AND'),
                );
              }}
              onBlur={() => {
                setTypeListFocused(false);
              }}
              onSelected={(item) => {
                setTypeListFocused(false);
                setType(`${item.name}`);
                setPromotionAutocompleteFilter(
                  setFilterColumn(
                    'type',
                    `${item.name}`,
                    { filter: promotionAutocompleteFilter },
                    'LIKE',
                    'AND',
                  ).filter,
                );
                setSearchFilter(
                  setFilterColumn(
                    'type',
                    `${item.name}`,
                    searchFilter,
                    'LIKE',
                    'AND',
                  ),
                );
              }}
            />

            <Form.Input.Dropdown
              className="form-banner-field"
              label="Promoção"
              errorMessage={false}
              items={getBanner('items.promotion')}
              value={promotionFilter}
              loading={autocompletePromotionLoading}
              hasClearItem
              disabled={isBatch}
              onChange={(e) => {
                setPromotionFilter(e);
              }}
              onFocus={() => {
                setPromotionListFocused(true);
                setPromotion('');
              }}
              onBlur={() => {
                setPromotionListFocused(false);
              }}
              onSelected={(item) => {
                setPromotionListFocused(false);
                setPromotion(`${item.name}`);
              }}
            />
          </>
        ) : null}
      </div>
      <Form.Input.Dropdown
        className="form-banner-field"
        errorMessage={false}
        value={
          getUnits().find(({ id }) => id === getShowSingleForm('product.unit'))
            ?.name
        }
        onSelected={(item) => {
          setBatchForm('product.unit', item.id as string);
        }}
        items={getUnits()}
        label="Unidade de Venda"
      />

      <Form.Input.Dropdown
        className="form-banner-field"
        errorMessage={false}
        value={
          descriptionTypeItems.find(
            ({ id }) => id === getShowSingleForm('product.duplicity'),
          )?.name
        }
        onSelected={(item) => {
          setBatchForm('product.duplicity', item.id as 'single' | 'double');
        }}
        items={descriptionTypeItems}
        label="Tipo de descrição"
      />

      <Form.Input
        className="form-banner-field"
        errorMessage={false}
        disabled={isBatch}
        value={getShowSingleForm('product.primary')}
        onChange={(e) => setBatchForm('product.primary', e.target.value)}
        label="Descrição Principal"
      />

      {duplicity === 'double' ? (
        <Form.Input
          className="form-banner-field"
          errorMessage={false}
          value={getShowSingleForm('product.secondary')}
          onChange={(e) => setBatchForm('product.secondary', e.target.value)}
          label="Descrição Secundária"
        />
      ) : null}

      <Element elements={elements} />
    </Form.Banner>
  );
}
